import { Star } from "lucide-react";

import { Badge, Checkbox } from "@/components/ui";
import { FileThumbnail } from "@/components/vault/FileThumbnail";
import type { VaultFileItem } from "@/types/vault.types";
import { cn } from "@/lib/utils";

interface VaultFileCardProps {
  file: VaultFileItem;
  selected: boolean;
  onOpen: () => void;
  onSelect: () => void;
}

export function VaultFileCard({ file, selected, onOpen, onSelect }: VaultFileCardProps) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      className={cn(
        "group relative flex cursor-pointer flex-col gap-2.5 rounded-2xl border bg-card/60 p-3 transition-colors",
        selected ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40",
      )}
    >
      <Checkbox
        checked={selected}
        onClick={(e) => e.stopPropagation()}
        onCheckedChange={onSelect}
        className="absolute top-2 left-2 z-10 opacity-0 group-hover:opacity-100 data-[checked]:opacity-100"
        aria-label={`Select ${file.name}`}
      />
      <div className="relative flex h-[84px] items-center justify-center rounded-md bg-muted/50">
        <FileThumbnail fileType={file.fileIcon} className="h-14 w-11 text-sm" />
        {file.starred && (
          <Star
            className="absolute right-1.5 bottom-1.5 size-3 fill-amber-400 text-amber-400"
            aria-hidden="true"
          />
        )}
      </div>
      <div className="min-w-0">
        <p className="truncate text-xs font-medium text-foreground">{file.name}</p>
        <p className="mt-1 text-[10.5px] text-muted-foreground">
          {file.sizeFormatted} · {file.date}
        </p>
        {file.tags && file.tags.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {file.tags.slice(0, 2).map((tag) => (
              <Badge key={tag} variant="outline" className="px-1 py-0 text-[9px]">
                {tag}
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
